import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import {
  Autocomplete,
  Button,
  FormLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../styles/propertyInformation.css";
import "../../../components/styles/popupStyle.css";
import GooglePlacesAutocomplete, {
  Place,
} from "../../subcomponents/GoogleAutocomplete";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import { removeSampleTestArray } from "../../actions/cleanup";
import AlertComponent from "../../subcomponents/AlertComponent";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { retailVendorSetupTooltip } from "../../../constants/TooltipInformation";
import { fieldValidationHelperText } from "../../../constants/validationHelperText";
import { RetailPropertyEnum } from "../../../constants/NavbarList";
import {
  validateEmail,
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const RetailVendorSetup = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [vendorType, setVendorType] = useState<string | null>(null);
  const [vendorCode, setVendorCode] = useState<string | null>(null);
  const [vendorName, setVendorName] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);
  const [addressLine1, setAddressLine1] = useState<string | null>(null);
  const [addressLine2, setAddressLine2] = useState<string | null>(null);
  const [city, setCity] = useState<string | null>(null);
  const [state, setState] = useState<string | null>(null);
  const [country, setCountry] = useState<string | null>(null);
  const [postalCode, setPostalCode] = useState<string | null>(null);
  const [phoneType, setPhoneType] = useState<string | null>(null);
  const [countryCode, setCountryCode] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [emailType, setEmailType] = useState<string | null>(null);
  const [emailAddress, setEmailAddress] = useState<string | null>(null);
  const [comments, setComments] = useState<string | null>(null);
  const [terms, setTerms] = useState<string | null>(null);

  const [vendorTypeErr, setVendorTypeErr] = useState<boolean>(false);
  const [vendorCodeErr, setVendorCodeErr] = useState<boolean>(false);
  const [vendorNameErr, setVendorNameErr] = useState<boolean>(false);
  const [firstNameErr, setFirstNameErr] = useState<boolean>(false);
  const [lastNameErr, setLastNameErr] = useState<boolean>(false);
  const [phoneTypeErr, setPhoneTypeErr] = useState<boolean>(false);
  const [countryCodeErr, setCountryCodeErr] = useState<boolean>(false);
  const [phoneErr, setPhoneErr] = useState<boolean>(false);
  const [emailTypeErr, setEmailTypeErr] = useState<boolean>(false);
  const [emailErr, setEmailErr] = useState<boolean>(false);
  const [commentsErr, setCommentsErr] = useState<boolean>(false);
  const [termsErr, setTermsErr] = useState<boolean>(false);

  const [retailVendorSetup, setRetailVendorSetup] = useState<any[]>([]);
  const [vendorTypeList, setVendorTypeList] = useState<string[]>([]);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const payload = {
    "Vendor Type": vendorType,
    "Vendor Code": vendorCode,
    "Vendor Name": vendorName,
    "First Name": firstName,
    "Last Name": lastName,
    "Address Line 1": addressLine1,
    "Address Line 2": addressLine2,
    City: city,
    State: state,
    Country: country,
    "Postal Code": postalCode,
    "Phone Type": phoneType,
    "Country Code": countryCode,
    "Phone Number": phoneNumber,
    "Email Type": emailType,
    "Email Address": emailAddress,
    Comments: comments,
    Terms: terms,
  };

  const validateInput = () => {
    setVendorTypeErr(false);
    setVendorCodeErr(false);
    setVendorNameErr(false);
    setFirstNameErr(false);
    setLastNameErr(false);
    setPhoneTypeErr(false);
    setCountryCodeErr(false);
    setPhoneErr(false);
    setEmailTypeErr(false);
    setEmailErr(false);
    setCommentsErr(false);
    setTermsErr(false);

    if (!vendorType || vendorType === "") {
      setVendorTypeErr(true);
      return false;
    }
    if (
      !validateString(
        vendorType,
        validationLength.vendorType,
        validationType.NAME,
      )
    ) {
      setVendorTypeErr(true);
      return false;
    }
    if (!vendorCode || String(vendorCode).trim() === "") {
      setVendorCodeErr(true);
      return false;
    }
    if (
      !validateString(
        vendorCode,
        validationLength.vendorCode,
        validationType.CODE,
      )
    ) {
      setVendorCodeErr(true);
      return false;
    }
    if (!vendorName || String(vendorName).trim() === "") {
      setVendorNameErr(true);
      return false;
    }
    if (
      !validateString(
        vendorName,
        validationLength.vendorName,
        validationType.NAME,
      )
    ) {
      setVendorNameErr(true);
      return false;
    }
    if (
      firstName &&
      String(firstName).trim() !== "" &&
      !validateString(
        firstName,
        validationLength.firstName,
        validationType.SPACE,
      )
    ) {
      setFirstNameErr(true);
      return false;
    }
    if (
      lastName &&
      String(lastName).trim() !== "" &&
      !validateString(lastName, validationLength.lastName, validationType.SPACE)
    ) {
      setLastNameErr(true);
      return false;
    }
    if (
      phoneType &&
      !validateString(
        phoneType,
        validationLength.lessonLocation,
        validationType.SPACE,
      )
    ) {
      setPhoneTypeErr(true);
      return false;
    }
    if (countryCode && countryCode !== "") {
      if (!/^\d+$/.test(countryCode) || countryCode.length > 4) {
        setCountryCodeErr(true);
        return false;
      }
    }
    if (phoneNumber && phoneNumber !== "") {
      if (!/^\d+$/.test(phoneNumber) || phoneNumber.length > 15) {
        setPhoneErr(true);
        return false;
      }
    }
    if (
      emailType &&
      !validateString(
        emailType,
        validationLength.lessonLocation,
        validationType.SPACE,
      )
    ) {
      setEmailTypeErr(true);
      return false;
    }
    if (emailAddress && emailAddress !== "" && !validateEmail(emailAddress)) {
      setEmailErr(true);
      return false;
    }
    if (
      comments &&
      !validateString(
        comments,
        validationLength.cancellationPolicy,
        validationType.NAME,
      )
    ) {
      setCommentsErr(true);
      return false;
    }
    if (
      terms &&
      !validateString(
        terms,
        validationLength.cancellationPolicy,
        validationType.NAME,
      )
    ) {
      setTermsErr(true);
      return false;
    }
    return true;
  };

  const editRetailVendorSetup = (list: any) => {
    if (list == null) {
      setSelectedId(null);
      setEditClicked(false);
      setVendorType(null);
      setVendorCode(null);
      setVendorName(null);
      setFirstName(null);
      setLastName(null);
      setAddressLine1(null);
      setAddressLine2(null);
      setCity(null);
      setState(null);
      setCountry(null);
      setPostalCode(null);
      setPhoneType(null);
      setCountryCode(null);
      setPhoneNumber(null);
      setEmailType(null);
      setEmailAddress(null);
      setComments(null);
      setTerms(null);
      setVendorTypeErr(false);
      setVendorCodeErr(false);
      setVendorNameErr(false);
      setFirstNameErr(false);
      setLastNameErr(false);
      setPhoneTypeErr(false);
      setCountryCodeErr(false);
      setPhoneErr(false);
      setEmailTypeErr(false);
      setEmailErr(false);
      setCommentsErr(false);
      setTermsErr(false);
      return;
    }
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setVendorType(list?.["Vendor Type"] ?? null);
    setVendorCode(list?.["Vendor Code"] ?? null);
    setVendorName(list?.["Vendor Name"] ?? null);
    setFirstName(list?.["First Name"] ?? null);
    setLastName(list?.["Last Name"] ?? null);
    setAddressLine1(list?.["Address Line 1"] ?? null);
    setAddressLine2(list?.["Address Line 2"] ?? null);
    setCity(list?.["City"] ?? null);
    setState(list?.["State"] ?? null);
    setCountry(list?.["Country"] ?? null);
    setPostalCode(list?.["Postal Code"] ?? null);
    setPhoneType(list?.["Phone Type"] ?? null);
    setCountryCode(
      list?.["Country Code"] != null ? String(list["Country Code"]) : null,
    );
    setPhoneNumber(
      list?.["Phone Number"] != null ? String(list["Phone Number"]) : null,
    );
    setEmailType(list?.["Email Type"] ?? null);
    setEmailAddress(list?.["Email Address"] ?? null);
    setComments(list?.["Comments"] ?? null);
    setTerms(list?.["Terms"] ?? null);
    setVendorTypeErr(false);
    setVendorCodeErr(false);
    setVendorNameErr(false);
    setFirstNameErr(false);
    setLastNameErr(false);
    setPhoneTypeErr(false);
    setCountryCodeErr(false);
    setPhoneErr(false);
    setEmailTypeErr(false);
    setEmailErr(false);
    setCommentsErr(false);
    setTermsErr(false);
  };

  const updateRetailVendorSetup = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(
        retailVendorSetup.filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.VENDORCODE,
        vendorCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setVendorTypeErr(false);
    setVendorCodeErr(false);
    setVendorNameErr(false);
    setFirstNameErr(false);
    setLastNameErr(false);
    setPhoneTypeErr(false);
    setCountryCodeErr(false);
    setPhoneErr(false);
    setEmailTypeErr(false);
    setEmailErr(false);
    setCommentsErr(false);
    setTermsErr(false);
    setRetailVendorSetup((prev: any[]) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editRetailVendorSetup(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${RetailPropertyEnum.vendorSetup} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addRetailVendorSetup = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(retailVendorSetup, DNDFieldsEnum.VENDORCODE, vendorCode)
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setRetailVendorSetup((prev: any[]) =>
      updateData(
        prev !== null && prev?.length
          ? [
              ...prev,
              {
                id: retailVendorSetup.length + 1,
                ...payload,
              },
            ]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editRetailVendorSetup(null);
    setEditClicked(false);
  };

  const deleteRetailVendorSetup = (id: number) => {
    setRetailVendorSetup(deleteItem(retailVendorSetup, id));
  };

  useEffect(() => {
    if (fieldValues) {
      setRetailVendorSetup(fieldValues[RetailPropertyEnum.vendorSetup]);
      setVendorTypeList(
        fillAutocompleteValues(
          fieldValues[RetailPropertyEnum.vendorTypes],
          DNDFieldsEnum.VENDORTYPE,
          "",
        ),
      );
    }
  }, []);

  useEffect(() => {
    data(retailVendorSetup);
    if (editClicked) {
      setSelectedId(
        checkData(retailVendorSetup, selectedId) ? selectedId : null,
      );
    }
  }, [retailVendorSetup]);

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Vendor Setup
              <Tooltip
                title={tooltipComponent(retailVendorSetupTooltip.header)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton click={() => editRetailVendorSetup(null)} />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editRetailVendorSetup(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Vendor Type
              <Tooltip
                title={tooltipComponent(retailVendorSetupTooltip.vendorType)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <Autocomplete
              id="retailVendorSetupVendorType"
              options={removeSampleTestArray(vendorTypeList ?? [])}
              value={vendorType ?? null}
              onChange={(_e, v) => {
                setVendorType(v);
                setVendorTypeErr(false);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Vendor Type"
                  error={vendorTypeErr}
                  helperText={
                    vendorTypeErr
                      ? !vendorType || vendorType === ""
                        ? validationMessages.fieldRequired
                        : validationMessages.retailVendorTypeName
                      : ""
                  }
                  fullWidth
                />
              )}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Vendor Code
              <Tooltip
                title={tooltipComponent(retailVendorSetupTooltip.vendorCode)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailVendorSetupVendorCode"
              variant="outlined"
              placeholder="Vendor Code"
              value={vendorCode ?? ""}
              helperText={
                vendorCodeErr
                  ? !vendorCode || String(vendorCode).trim() === ""
                    ? validationMessages.fieldRequired
                    : validationMessages.vendorCode
                  : fieldValidationHelperText.retailVendorSetupVendorCode
              }
              error={vendorCodeErr}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setVendorCode(e.target.value || null);
                setVendorCodeErr(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Vendor Name
              <Tooltip
                title={tooltipComponent(retailVendorSetupTooltip.vendorName)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailVendorSetupVendorName"
              variant="outlined"
              placeholder="Vendor Name"
              value={vendorName ?? ""}
              helperText={
                vendorNameErr
                  ? !vendorName || String(vendorName).trim() === ""
                    ? validationMessages.fieldRequired
                    : validationMessages.retailVendorSetupVendorName
                  : fieldValidationHelperText.retailVendorSetupVendorName
              }
              error={vendorNameErr}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setVendorName(e.target.value || null);
                setVendorNameErr(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              First Name
              <Tooltip
                title={tooltipComponent(retailVendorSetupTooltip.firstName)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailVendorSetupFirstName"
              variant="outlined"
              placeholder="First Name"
              value={firstName ?? ""}
              helperText={
                firstNameErr
                  ? validationMessages.firstName
                  : fieldValidationHelperText.retailVendorSetupFirstName
              }
              error={firstNameErr}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFirstName(e.target.value || null);
                setFirstNameErr(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Last Name
              <Tooltip
                title={tooltipComponent(retailVendorSetupTooltip.lastName)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailVendorSetupLastName"
              variant="outlined"
              placeholder="Last Name"
              value={lastName ?? ""}
              helperText={
                lastNameErr
                  ? validationMessages.lastName
                  : fieldValidationHelperText.retailVendorSetupLastName
              }
              error={lastNameErr}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setLastName(e.target.value || null);
                setLastNameErr(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Address Line 1
              <Tooltip
                title={tooltipComponent(retailVendorSetupTooltip.addressLine1)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <Box className="property-information-search-wrap">
              <GooglePlacesAutocomplete
                apiKey={process.env.REACT_APP_GOOGLE_API_KEY ?? ""}
                placeholder="Search for the address"
                onPlaceSelected={(place: Place) => {
                  if (!place.addressComponents) return;
                  let nextAddressLine1 = "";
                  let nextAddressLine2 = "";
                  let nextCity = "";
                  let nextState = "";
                  let nextCountry = "";
                  let nextZipCode = "";

                  place.addressComponents.forEach((component: any) => {
                    const primaryType = component.types[0];

                    switch (primaryType) {
                      case "street_number":
                        nextAddressLine1 =
                          component.long_name + " " + nextAddressLine1;
                        break;
                      case "route":
                        nextAddressLine1 =
                          nextAddressLine1 + component.long_name;
                        break;
                      case "sublocality":
                      case "sublocality_level_1":
                      case "neighborhood":
                      case "subpremise":
                        nextAddressLine2 = component.long_name;
                        break;
                      case "locality":
                        nextCity = component.long_name;
                        break;
                      case "administrative_area_level_1":
                        nextState = component.long_name;
                        break;
                      case "country":
                        nextCountry = component.long_name;
                        break;
                      case "postal_code":
                        nextZipCode = component.long_name;
                        break;
                      default:
                        break;
                    }
                  });

                  setAddressLine1(nextAddressLine1.trim());
                  if (nextAddressLine2)
                    setAddressLine2(nextAddressLine2.trim());
                  if (nextCity) setCity(nextCity);
                  if (nextState) setState(nextState);
                  if (nextCountry) setCountry(nextCountry);
                  if (nextZipCode) setPostalCode(nextZipCode);
                }}
              />
              <SearchOutlinedIcon
                className="property-information-search-icon"
                fontSize="small"
              />
            </Box>
            <TextField
              id="retailVendorSetupAddressLine1"
              variant="outlined"
              placeholder="Address Line 1"
              value={addressLine1 ?? ""}
              helperText={
                fieldValidationHelperText.retailVendorSetupAddressField
              }
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setAddressLine1(e.target.value || null);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Address Line 2
              <Tooltip
                title={tooltipComponent(retailVendorSetupTooltip.addressLine2)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailVendorSetupAddressLine2"
              variant="outlined"
              placeholder="Address Line 2"
              value={addressLine2 ?? ""}
              helperText={
                fieldValidationHelperText.retailVendorSetupAddressField
              }
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setAddressLine2(e.target.value || null);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              City
              <Tooltip title={tooltipComponent(retailVendorSetupTooltip.city)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailVendorSetupCity"
              variant="outlined"
              placeholder="City"
              value={city ?? ""}
              helperText={
                fieldValidationHelperText.retailVendorSetupAddressField
              }
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setCity(e.target.value || null);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              State
              <Tooltip title={tooltipComponent(retailVendorSetupTooltip.state)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailVendorSetupState"
              variant="outlined"
              placeholder="State"
              value={state ?? ""}
              helperText={
                fieldValidationHelperText.retailVendorSetupAddressField
              }
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setState(e.target.value || null);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Country
              <Tooltip
                title={tooltipComponent(retailVendorSetupTooltip.country)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailVendorSetupCountry"
              variant="outlined"
              placeholder="Country"
              value={country ?? ""}
              helperText={
                fieldValidationHelperText.retailVendorSetupAddressField
              }
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setCountry(e.target.value || null);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Postal Code
              <Tooltip
                title={tooltipComponent(retailVendorSetupTooltip.postalCode)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailVendorSetupPostalCode"
              variant="outlined"
              placeholder="Postal Code"
              value={postalCode ?? ""}
              helperText={fieldValidationHelperText.retailVendorSetupPostalCode}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setPostalCode(e.target.value || null);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Phone Type
              <Tooltip
                title={tooltipComponent(retailVendorSetupTooltip.phoneType)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailVendorSetupPhoneType"
              variant="outlined"
              placeholder="Phone Type"
              value={phoneType ?? ""}
              helperText={
                phoneTypeErr
                  ? validationMessages.lessonLocation
                  : fieldValidationHelperText.retailVendorSetupPhoneEmailType
              }
              error={phoneTypeErr}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setPhoneType(e.target.value || null);
                setPhoneTypeErr(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Country Code
              <Tooltip
                title={tooltipComponent(retailVendorSetupTooltip.countryCode)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailVendorSetupCountryCode"
              variant="outlined"
              placeholder="Country Code"
              value={countryCode ?? ""}
              helperText={
                countryCodeErr
                  ? "Enter numbers only (max 4)"
                  : fieldValidationHelperText.retailVendorSetupCountryCode
              }
              error={countryCodeErr}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const v = e.target.value.replace(/\D/g, "");
                setCountryCode(v || null);
                setCountryCodeErr(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Phone Number
              <Tooltip
                title={tooltipComponent(retailVendorSetupTooltip.phoneNumber)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailVendorSetupPhoneNumber"
              variant="outlined"
              placeholder="Phone Number"
              value={phoneNumber ?? ""}
              helperText={
                phoneErr
                  ? "Enter numbers only (max 15)"
                  : fieldValidationHelperText.retailVendorSetupPhoneNumber
              }
              error={phoneErr}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const v = e.target.value.replace(/\D/g, "");
                setPhoneNumber(v || null);
                setPhoneErr(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Email Type
              <Tooltip
                title={tooltipComponent(retailVendorSetupTooltip.emailType)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailVendorSetupEmailType"
              variant="outlined"
              placeholder="Email Type"
              value={emailType ?? ""}
              helperText={
                emailTypeErr
                  ? validationMessages.lessonLocation
                  : fieldValidationHelperText.retailVendorSetupPhoneEmailType
              }
              error={emailTypeErr}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setEmailType(e.target.value || null);
                setEmailTypeErr(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Email Address
              <Tooltip
                title={tooltipComponent(retailVendorSetupTooltip.emailAddress)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailVendorSetupEmailAddress"
              variant="outlined"
              placeholder="Email Address"
              value={emailAddress ?? ""}
              helperText={
                emailErr
                  ? validationMessages.email
                  : fieldValidationHelperText.retailVendorSetupEmailAddress
              }
              error={emailErr}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setEmailAddress(e.target.value || null);
                setEmailErr(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Comments
              <Tooltip
                title={tooltipComponent(retailVendorSetupTooltip.comments)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailVendorSetupComments"
              variant="outlined"
              placeholder="Comments"
              value={comments ?? ""}
              helperText={
                commentsErr
                  ? validationMessages.cancellationPolicy
                  : fieldValidationHelperText.retailVendorSetupMultiline
              }
              error={commentsErr}
              fullWidth
              multiline
              minRows={2}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setComments(e.target.value || null);
                setCommentsErr(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Terms
              <Tooltip title={tooltipComponent(retailVendorSetupTooltip.terms)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailVendorSetupTerms"
              variant="outlined"
              placeholder="Terms"
              value={terms ?? ""}
              helperText={
                termsErr
                  ? validationMessages.cancellationPolicy
                  : fieldValidationHelperText.retailVendorSetupMultiline
              }
              error={termsErr}
              fullWidth
              multiline
              minRows={2}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTerms(e.target.value || null);
                setTermsErr(false);
              }}
            />
          </Stack>

          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={
                selectedId ? updateRetailVendorSetup : addRetailVendorSetup
              }
              id="addRetailVendorSetup"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Vendor
            </Button>
          </Stack>

          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">Vendors Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {retailVendorSetup?.length ? (
                <DraggableItemList
                  itemData={retailVendorSetup}
                  onOrderUpdate={(order) => setRetailVendorSetup(order)}
                  onEdit={(info) => {
                    editRetailVendorSetup(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteRetailVendorSetup(id)}
                  code={DNDFieldsEnum.VENDORCODE}
                  name={DNDFieldsEnum.VENDORNAME}
                />
              ) : (
                <Typography variant="body2">
                  No Vendor Setup Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default RetailVendorSetup;
